import { Static, Type } from '@sinclair/typebox'
import { isBlankValue } from './prop-value'

/**
 * inputUiInfo key recording WHICH input a step's last test ran with. Changing
 * a value after the test (another form, another spreadsheet) leaves the old
 * sample data in place: without this stamp the AI Builder keeps trusting
 * evidence that describes a configuration the step no longer has.
 */
export const AI_BUILDER_TESTED_INPUT = 'aiBuilderTestedInput'

/**
 * Whether a step's test evidence is a real run or a synthetic example the
 * wizard generated because no real one existed yet (an empty Kizeo form's
 * schema mock, `trigger-mock.ts`). A GENERATED example's shape is a guess —
 * the model must not read it as proof a field exists or holds a given type.
 */
export const EvidenceSource = Type.Union([Type.Literal('test'), Type.Literal('generated')])
export type EvidenceSource = Static<typeof EvidenceSource>

/**
 * Bound to the test's lastTestDate: a test that wrote no stamp moves
 * lastTestDate on, and the stamp then no longer speaks for the evidence.
 */
export const TestedInputStamp = Type.Object({
    testedAt: Type.String(),
    inputHash: Type.String(),
    /** Absent on a stamp written before this field existed — reads as 'test' */
    evidence: Type.Optional(EvidenceSource),
})
export type TestedInputStamp = Static<typeof TestedInputStamp>

type StampedStep = {
    input?: unknown
    inputUiInfo?: { lastTestDate?: string } & Record<string, unknown>
}

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193
const HEX_RADIX = 16

/**
 * Key order and empty values carry no meaning: the panel form materialises
 * unset props as undefined, null or '' without changing what the step does.
 */
const canonicalize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (typeof value !== 'object' || value === null) return value
    const record = value as Record<string, unknown>
    const canonical: Record<string, unknown> = {}
    for (const key of Object.keys(record).sort()) {
        if (isBlankValue(record[key])) continue
        canonical[key] = canonicalize(record[key])
    }
    return canonical
}

/** Whether two inputs do the same thing, by the canonical form the stamp hashes */
export const isSameInput = (left: unknown, right: unknown): boolean => {
    const canonicalText = (value: unknown): string | undefined =>
        isBlankValue(value) ? undefined : JSON.stringify(canonicalize(value))
    return canonicalText(left) === canonicalText(right)
}

/** A hash, not a copy: the input may hold long texts the stamp must not double */
const hashInput = (input: unknown): string => {
    const text = JSON.stringify(canonicalize(input ?? {})) ?? ''
    let hash = FNV_OFFSET_BASIS
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index)
        hash = Math.imul(hash, FNV_PRIME)
    }
    return (hash >>> 0).toString(HEX_RADIX)
}

export const stampTestedInput = (
    input: unknown,
    testedAt: string,
    evidence: EvidenceSource,
): TestedInputStamp => ({
    testedAt,
    inputHash: hashInput(input),
    evidence,
})

const readStamp = (
    inputUiInfo: Record<string, unknown> | undefined,
): TestedInputStamp | null => {
    const stamp = inputUiInfo?.[AI_BUILDER_TESTED_INPUT]
    if (typeof stamp !== 'object' || stamp === null) return null
    const { testedAt, inputHash, evidence } = stamp as Record<string, unknown>
    if (typeof testedAt !== 'string' || typeof inputHash !== 'string') {
        return null
    }
    return { testedAt, inputHash, ...(evidence === 'generated' ? { evidence } : {}) }
}

/**
 * True only when the stamp provably describes the CURRENT test (bound to
 * `lastTestDate`, the same guard `isTestEvidenceStale` reads) AND that test
 * was a generated example. A stamp written before this field existed, or
 * one whose test has since moved on, reads as a real test — never a guess.
 */
export const isGeneratedEvidence = (step: StampedStep): boolean => {
    const stamp = readStamp(step.inputUiInfo)
    return stamp !== null && stamp.testedAt === step.inputUiInfo?.lastTestDate && stamp.evidence === 'generated'
}

/**
 * True only when the stamp provably describes the last test AND the input
 * has changed since. No stamp, or a stamp from an earlier test, proves
 * nothing — the evidence is trusted as before.
 */
export const isTestEvidenceStale = (step: StampedStep): boolean => {
    const stamp = readStamp(step.inputUiInfo)
    if (!stamp || stamp.testedAt !== step.inputUiInfo?.lastTestDate) {
        return false
    }
    return stamp.inputHash !== hashInput(step.input)
}
